import {
  CallEffect,
  PutEffect,
  SelectEffect,
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import axios from 'axios';
import { toastStyles } from '../../utils/utils';
import {
  CreateFolderPayload,
  IAddActivityLibraryGraphQLResponse,
  IAddActivityLibraryPayload,
  RenameFolderPayload,
  addActivity,
  addActivityFailure,
  addActivitySuccess,
  editActivity,
  editActivityFailure,
  editActivitySuccess,
  getUserFoldersAndActivities,
  createNewFolderSuccess,
  createNewFolderFailure,
  renameFolderSuccess,
  renameFolderFailure,
  createNewFolder,
  renameFolder,
  IGetUserFoldersAndActivitiesGraphQLResponse,
  getUserFoldersAndActivitiesSuccess,
  getUserFoldersAndActivitiesFailure,
  getActivitiesInsideFolder,
  getActivitiesInsideFolderSuccess,
  getActivitiesInsideFolderFailure,
  IGetActivitiesInsideFolderGraphQLResponse,
  deleteActivity,
  IDeleteActivityLPayload,
  deleteActivitySuccess,
  deleteActivityFailure,
  deleteFolder,
  IDeleteFolderLPayload,
  deleteFolderSuccess,
  deleteFolderFailure,
  restoreActivity,
  restoreActivitySuccess,
  restoreActivityFailure,
  IRestoreActivityLPayload,
  clearActivitiesInsideFolder,
  permenantlyDeleteActivitySuccess,
  permenantlyDeleteActivityFailure,
  permenantlyDeleteActivity,
  checkActivityLibraryExistence,
  ICheckActivityLibraryExistenceResponse,
  checkActivityLibraryExistenceSuccess,
  checkActivityLibraryExistenceFailure,
  IEditActivityLibraryGraphQLResponse,
  IDeleteFolderGraphQLResponse,
  removeFolderFromUserFolders,
  IRenameFolderGraphQLResponse,
  updateFolderNameUserFolders,
} from './activityLibrarySlice';
import client from '../../service/graphql';
import {
  ADD_NEW_ACTIVITY,
  CREATE_NEW_FOLDER,
  DELETE_ACTIVITY,
  DELETE_FOLDER,
  GET_ACTIVITIES_INSIDE_FOLDER,
  GET_USER_FOLDERS_AND_ACTIVITIES,
  RENAME_FOLDER,
  EDIT_ACTIVITY,
  PERMANENTLY_DELETE_ACTIVITY,
  CHECK_ACTIVITY_LIBRARY_EXISTENCE,
} from '../../service/graphql/activityLibraryQuery';
import i18n from '../../i18n';
import { IFileObject, IGetPreSignedUrlResponse } from '../activity/activitySaga';
import { IGraphQLResponse } from '../../types/commonTypes';
import { IStepMediaAsset } from '../activity/activitySlice';

export async function saveActivityStepMediaInS3({ fileObject }: { fileObject: IFileObject }) {
  try {
    const response = await axios.put(fileObject.link, fileObject.assetFile, {
      headers: {
        'Content-Type': `${fileObject.assetFile.type}`,
      },
    });
    if (response.status === 200) {
      return {
        success: true,
        url: fileObject.link,
      };
    }
    return {
      success: false,
      url: fileObject.link,
      error: response.statusText,
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      return {
        success: false,
        url: fileObject.link,
        error: error.message,
      };
    }
  }
}

function* watchCreateNewFolder({ payload }: PayloadAction<CreateFolderPayload>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: CREATE_NEW_FOLDER,
      variables: { ...payload },
    });
    toast.success(
      i18n.t('activityLibrary.createRenameFolderModal.toastMessage.createSuccess'),
      toastStyles.success
    );
    yield put(createNewFolderSuccess());
    yield put(getUserFoldersAndActivities({ coachId: payload.coachId }));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(createNewFolderFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* saveActivityStepMediaInS3Saga(fileObject: IFileObject) {
  yield call(saveActivityStepMediaInS3, { fileObject });
}

function* watchAddActivity({ payload }: PayloadAction<IAddActivityLibraryPayload>) {
  try {
    const { activityInput, stepMediaAssets } = payload;

    const result: IGraphQLResponse<IAddActivityLibraryGraphQLResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: ADD_NEW_ACTIVITY,
        variables: activityInput,
      }
    );
    if (result.data.addActivityLibrary.activityStep.length > 0) {
      const preSignedUrls: IGetPreSignedUrlResponse[] = result.data.addActivityLibrary.activityStep
        .filter((stepURL: IGetPreSignedUrlResponse) => stepURL.asset !== '')
        .map((stepURL: IGetPreSignedUrlResponse) => ({
          link: stepURL.link,
          asset: stepURL.asset,
        }));

      const fileWithPresignedUrls: IFileObject[] = stepMediaAssets
        .map((mediaAsset: IStepMediaAsset, index: number) =>
          mediaAsset.assetFile !== null
            ? {
                link: preSignedUrls[index].link,
                assetFile: mediaAsset.assetFile,
                asset: preSignedUrls[index].asset,
              }
            : null
        )
        .filter((fileObject): fileObject is IFileObject => fileObject !== null);

      yield all(
        fileWithPresignedUrls.map((fileObject) => call(saveActivityStepMediaInS3Saga, fileObject))
      );
    }

    toast.success(i18n.t('activityLibrary.toastMessages.activityCreated'), toastStyles.success);
    yield put(addActivitySuccess());
    const coachId = yield select((state) => state.coach.coach?.id);
    if (activityInput.folderId) {
      yield put(clearActivitiesInsideFolder());
      yield put(
        getActivitiesInsideFolder({
          coachId,
          filterValue: '',
          folderId: activityInput.folderId,
          page: 1,
          pageSize: 24,
        })
      );
    } else {
      yield put(getUserFoldersAndActivities({ coachId, filterValue: '' }));
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(addActivityFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchEditActivity({ payload }: PayloadAction<IAddActivityLibraryPayload>) {
  try {
    const { activityInput, stepMediaAssets } = payload;

    const result: IGraphQLResponse<IEditActivityLibraryGraphQLResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: EDIT_ACTIVITY,
        variables: activityInput,
      }
    );
    if (result.data.editActivityLibrary.activityStep.length > 0) {
      const preSignedUrls: IGetPreSignedUrlResponse[] = result.data.editActivityLibrary.activityStep
        .filter((stepURL: IGetPreSignedUrlResponse) => stepURL.asset !== '')
        .map((stepURL: IGetPreSignedUrlResponse) => ({
          link: stepURL.link,
          asset: stepURL.asset,
        }));

      const fileWithPresignedUrls: IFileObject[] = stepMediaAssets
        .map((mediaAsset: IStepMediaAsset, index: number) =>
          mediaAsset.assetFile !== null
            ? {
                link: preSignedUrls[index].link,
                assetFile: mediaAsset.assetFile,
                asset: preSignedUrls[index].asset,
              }
            : null
        )
        .filter((fileObject): fileObject is IFileObject => fileObject !== null);

      yield all(
        fileWithPresignedUrls.map((fileObject) => call(saveActivityStepMediaInS3Saga, fileObject))
      );
    }

    toast.success(i18n.t('activityLibrary.toastMessages.activityUpdated'), toastStyles.success);
    yield put(editActivitySuccess());
    const coachId = yield select((state) => state.coach.coach?.id);
    if (activityInput.folderId) {
      yield put(clearActivitiesInsideFolder());
      yield put(
        getActivitiesInsideFolder({
          coachId,
          filterValue: '',
          folderId: activityInput.folderId,
          page: 1,
          pageSize: 24,
        })
      );
    } else {
      yield put(getUserFoldersAndActivities({ coachId, filterValue: '' }));
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(editActivityFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchGetUserFoldersAndActivities({
  payload,
}: PayloadAction<{ coachId: string; filterValue?: string }>) {
  try {
    const { coachId, filterValue } = payload;

    const result: IGraphQLResponse<IGetUserFoldersAndActivitiesGraphQLResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_USER_FOLDERS_AND_ACTIVITIES,
        variables: { coachId, filterValue },
      }
    );
    yield put(getUserFoldersAndActivitiesSuccess(result.data.getUserFoldersAndActivities));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getUserFoldersAndActivitiesFailure());
    }
  }
}

function* watchRenameFolder({ payload }: PayloadAction<RenameFolderPayload>) {
  try {
    const result: IGraphQLResponse<IRenameFolderGraphQLResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: RENAME_FOLDER,
        variables: { ...payload },
      }
    );
    toast.success(
      i18n.t('activityLibrary.createRenameFolderModal.toastMessage.renameSuccess'),
      toastStyles.success
    );
    yield put(renameFolderSuccess());
    yield put(getUserFoldersAndActivities({ coachId: payload.coachId }));
    yield put(updateFolderNameUserFolders({ ...result.data.editFolder }));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(renameFolderFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchGetActivitiesInsideFolder({
  payload,
}: PayloadAction<{
  coachId: string;
  filterValue?: string;
  folderId: string;
  page: number;
  pageSize: number;
}>) {
  try {
    const { coachId, filterValue, folderId, page, pageSize } = payload;

    const result: IGraphQLResponse<IGetActivitiesInsideFolderGraphQLResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_ACTIVITIES_INSIDE_FOLDER,
        variables: { coachId, filterValue, folderId, page, pageSize },
      }
    );
    yield put(getActivitiesInsideFolderSuccess(result.data.getActivityLibraries));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getActivitiesInsideFolderFailure());
    }
  }
}

function* watchDeleteActivity({ payload }: PayloadAction<IDeleteActivityLPayload>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: DELETE_ACTIVITY,
      variables: { ...payload },
    });
    yield put(deleteActivitySuccess());
    const successMsg = payload.isMoveToTrash
      ? i18n.t('activityLibrary.toastMessages.activityDeleted')
      : i18n.t('activityLibrary.toastMessages.activityMoved');
    const toastStyle = payload.isMoveToTrash ? toastStyles.error : toastStyles.success;
    toast.success(successMsg, toastStyle);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(deleteActivityFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchDeleteFolder({ payload }: PayloadAction<IDeleteFolderLPayload>) {
  try {
    const result: IGraphQLResponse<IDeleteFolderGraphQLResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: DELETE_FOLDER,
        variables: { ...payload },
      }
    );
    yield put(deleteFolderSuccess());
    if (payload.isEmptyTrash) {
      toast.success(i18n.t('activityLibrary.toastMessages.trashCleared'), toastStyles.error);
      yield put(clearActivitiesInsideFolder());
      yield put(
        getActivitiesInsideFolder({
          coachId: payload.coachId,
          filterValue: '',
          folderId: payload.folderId,
          page: 1,
          pageSize: 24,
        })
      );
    } else {
      toast.success(i18n.t('activityLibrary.toastMessages.folderDeleted'), toastStyles.error);
      yield put(getUserFoldersAndActivities({ coachId: payload.coachId, filterValue: '' }));
      yield put(removeFolderFromUserFolders(result.data.deleteFolder.id));
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(deleteFolderFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchRestoreActivity({ payload }: PayloadAction<IRestoreActivityLPayload>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: DELETE_ACTIVITY,
      variables: { ...payload, folderId: null },
    });
    toast.success(i18n.t('activityLibrary.toastMessages.activityRestored'), toastStyles.success);
    yield put(restoreActivitySuccess());
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(restoreActivityFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchPermanentlyDeleteActivity({ payload }: PayloadAction<{ id: string }>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: PERMANENTLY_DELETE_ACTIVITY,
      variables: { ...payload },
    });
    toast.success(
      i18n.t('activityLibrary.toastMessages.activityPermenantlyDeleted'),
      toastStyles.error
    );
    yield put(permenantlyDeleteActivitySuccess());
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(permenantlyDeleteActivityFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchCheckActivityLibraryExistence({
  payload,
}: PayloadAction<{ activityTitle: string; activityId?: string }>): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  IGraphQLResponse<ICheckActivityLibraryExistenceResponse>
> {
  const currentCoachId = yield select((state) => state.coach.coach?.id);
  const { activityTitle, activityId } = payload;
  try {
    const result: IGraphQLResponse<ICheckActivityLibraryExistenceResponse> = yield call(
      client.query.bind(client),
      {
        query: CHECK_ACTIVITY_LIBRARY_EXISTENCE,
        variables: {
          checkActivityLibraryExistenceInput: {
            name: activityTitle,
            coachId: currentCoachId,
            activityId,
          },
        },
      }
    );

    yield put(
      checkActivityLibraryExistenceSuccess(result.data.checkActivityLibraryExistence.success)
    );
  } catch (error) {
    if (error instanceof Error) yield put(checkActivityLibraryExistenceFailure());
  }
}

export function* watchActivityLibrarySagas() {
  yield takeLatest(addActivity, watchAddActivity);
  yield takeLatest(editActivity, watchEditActivity);
  yield takeLatest(getUserFoldersAndActivities, watchGetUserFoldersAndActivities);
  yield takeLatest(createNewFolder, watchCreateNewFolder);
  yield takeLatest(renameFolder, watchRenameFolder);
  yield takeLatest(getActivitiesInsideFolder, watchGetActivitiesInsideFolder);
  yield takeLatest(deleteActivity, watchDeleteActivity);
  yield takeLatest(deleteFolder, watchDeleteFolder);
  yield takeLatest(restoreActivity, watchRestoreActivity);
  yield takeLatest(permenantlyDeleteActivity, watchPermanentlyDeleteActivity);
  yield takeLatest(checkActivityLibraryExistence, watchCheckActivityLibraryExistence);
}

const activityLibrarySagas = watchActivityLibrarySagas;
export default activityLibrarySagas;
